/**
 * Reusable SEO composable that standardizes title, description, canonical URL,
 * Open Graph tags, Twitter cards, and optional JSON-LD structured data across
 * all pages.
 */

interface SEOOptions {
    /** Page title (without site suffix). The suffix " | Unified" is appended automatically unless `noSuffix` is set. */
    title: string;
    /** Use `title` as-is without the site suffix (e.g. the home page, whose title already leads with the brand). */
    noSuffix?: boolean;
    /** Meta description. Also used for og:description and twitter:description. */
    description: string;
    /** Optional absolute OG image URL. Falls back to the default Unified logo. */
    image?: string;
    /** og:image:alt text. Defaults to the page title when a custom `image` is given. */
    imageAlt?: string;
    /** og:image:width in pixels. Only applied when known (defaults are set for the default logo). */
    imageWidth?: number;
    /** og:image:height in pixels. Only applied when known (defaults are set for the default logo). */
    imageHeight?: number;
    /** og:image:type MIME type, e.g. 'image/png'. Only applied when known. */
    imageType?: string;
    /** og:type value. Defaults to 'website'. */
    type?: string;
    /** One or more JSON-LD objects to inject as <script type="application/ld+json">. */
    jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE_BASE = 'https://unified.to';
const DEFAULT_IMAGE = `${SITE_BASE}/logos/Unified_Black.png`;
const DEFAULT_IMAGE_ALT = 'Unified logo';
const DEFAULT_IMAGE_WIDTH = 64;
const DEFAULT_IMAGE_HEIGHT = 66;
const DEFAULT_IMAGE_TYPE = 'image/png';
const SITE_SUFFIX = ' | Unified';

export function useSEO(opts: SEOOptions) {
    const route = useRoute();

    const canonicalUrl = computed(() => {
        // Remove trailing slashes except for root, and strip query/hash
        const path = route.path === '/' ? '/' : route.path.replace(/\/+$/, '');
        return `${SITE_BASE}${path}`;
    });

    const fullTitle = computed(() => {
        if (opts.noSuffix || opts.title.includes('|')) return opts.title;
        return `${opts.title}${SITE_SUFFIX}`;
    });

    const usingDefaultImage = !opts.image;
    const image = opts.image || DEFAULT_IMAGE;
    // Dimensions/type/alt below are only known for certain, so they're only supplied for the
    // default logo unless a caller passing a custom `image` also supplies its own metadata —
    // otherwise we'd publish incorrect og:image:width/height for that image.
    const imageAlt = opts.imageAlt ?? (usingDefaultImage ? DEFAULT_IMAGE_ALT : opts.title);
    const imageType = opts.imageType ?? (usingDefaultImage ? DEFAULT_IMAGE_TYPE : undefined);
    const imageWidth = opts.imageWidth ?? (usingDefaultImage ? DEFAULT_IMAGE_WIDTH : undefined);
    const imageHeight = opts.imageHeight ?? (usingDefaultImage ? DEFAULT_IMAGE_HEIGHT : undefined);

    const scripts: Array<{ type: string; key: string; textContent: string }> = [];
    if (opts.jsonLd) {
        const items = Array.isArray(opts.jsonLd) ? opts.jsonLd : [opts.jsonLd];
        items.forEach((ld, i) => {
            scripts.push({
                type: 'application/ld+json',
                key: `seo-jsonld-${i}`,
                textContent: JSON.stringify(ld),
            });
        });
    }

    useHead({
        title: fullTitle.value,
        link: [{ rel: 'canonical', href: canonicalUrl.value }],
        meta: [
            { name: 'description', content: opts.description },
            // Open Graph
            { property: 'og:title', content: opts.title },
            { property: 'og:description', content: opts.description },
            { property: 'og:image', content: image },
            ...(imageType ? [{ property: 'og:image:type', content: imageType }] : []),
            ...(imageWidth ? [{ property: 'og:image:width', content: String(imageWidth) }] : []),
            ...(imageHeight ? [{ property: 'og:image:height', content: String(imageHeight) }] : []),
            ...(imageAlt ? [{ property: 'og:image:alt', content: imageAlt }] : []),
            { property: 'og:type', content: opts.type || 'website' },
            { property: 'og:url', content: canonicalUrl.value },
            // Twitter
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:title', content: opts.title },
            { name: 'twitter:description', content: opts.description },
            { name: 'twitter:image', content: image },
        ],
        script: scripts,
    });
}
